/**
 * Buying Session field definitions — shared between ParamsSidebar
 * (renders the controls), Settings (per-user prefs editor), and
 * Day 73 P4.1 scenario cleanup / drift detection.
 *
 * FILTER_FIELDS + WEIGHT_COMPONENTS are user-customizable from
 * Settings → Buying Session Preferences.
 *
 * CONSTRAINT_FIELDS is co-located here for shared key-list access
 * from scenarioCleanup, BUT Settings → BSP currently customizes
 * only filters and weights — constraints stay non-customizable in
 * V1 by design (the 4 entries below are operator-tactical knobs,
 * not preference-tunable).
 *
 * NULL on `profiles.buying_session_prefs` means "use the source order
 * defined here". Extracted from ParamsSidebar.tsx on Day 55.
 * CONSTRAINT_FIELDS + computeParamDefaults added Day 73 P4.1.
 *
 * Adding entries: append to the corresponding array; existing user
 * prefs JSONB will silently ignore unknown keys until they're re-saved
 * with the new defaults.
 */

import type { ParamRow } from '../hooks/useParamStore';
import type { RiskFactorKey } from './poConstraints';
import type {
  BuyingSessionPrefs,
  BuyingSessionFilterPref,
  BuyingSessionWeightPref,
  BuyingSessionConstraintPref,
  BuyingSessionRiskPref,
  BuyScoreWeightComponent,
} from '../types';

export interface FilterDef {
  key: string;
  label: string;
  unit: string;
  /** Numeric is the default. 'boolean' is used for the my_products_only
   *  CONSTRAINT_FIELDS entry — filters and weights are always numeric in V1. */
  kind?: 'numeric' | 'boolean';
  /** Day 95 H.4 — false hides the filter from the sidebar + Settings BSP.
   *  Filters render from FILTER_FIELDS, NOT buy_score_weight_components, so this
   *  is the correct hide lever (see #DBN-95-1). Flip to true / remove when the
   *  filter has a real upstream column. */
  available?: boolean;
  /** Day 95 H.2 — optional muted helper-line text rendered beneath a filter
   *  row (e.g. the min_demand_score ordinal-scale legend). */
  hint?: string;
}

// WeightDef interface retired Day 91a.5 P2E alongside WEIGHT_COMPONENTS
// export (zero post-deletion consumers per Convention 37 pre-deletion grep).
// Type-graduation target: BuyScoreWeightComponent at src/types/index.ts
// (Row<'buy_score_weight_components'> from database.types.ts; superset shape
// covering all pre-graduation WeightDef use cases — key + label + penalty
// all present plus is_available + sort_order + applies_to_metric + notes +
// tooltip_text + id + created_at + updated_at). Admin source: bswc table
// via useSettingsConfig.buyScoreWeightComponents per Day 91a.5 P2A 5th
// parallel loader extension.

export const FILTER_FIELDS: FilterDef[] = [
  { key: 'min_roi_pct', label: 'Min ROI%', unit: '%' },
  { key: 'min_margin_pct', label: 'Min Margin%', unit: '%' },
  { key: 'min_profit_unit', label: 'Min Profit/Unit', unit: '$' },
  { key: 'max_returns_rate', label: 'Max Returns Rate', unit: '%', available: false }, // no per-product returns-rate column; see #DBN-95-1 / Day 98b F1
  {
    key: 'min_demand_score',
    label: 'Min Demand Score',
    unit: '',
    hint: '1 = Low · 2 = Med · 3 = High. Higher levels may show no products on current data.',
  },
  { key: 'max_competition', label: 'Max Competition', unit: '' },
  { key: 'min_bsr', label: 'Min BSR', unit: '', available: false }, // until Keepa lands
  { key: 'min_price', label: 'Min Price', unit: '$' },
  { key: 'max_price', label: 'Max Price', unit: '$' },
  { key: 'min_velocity', label: 'Min Velocity', unit: '/mo' },
];

// WEIGHT_COMPONENTS export retired Day 91a.5 P2E per R-Lock-7 + R-Lock-16
// global src/ grep gate. Admin source graduated to buy_score_weight_components
// table (seeded Day 67 P2.2; 25 rows; 7 is_available=true wired keys per Day 68
// P3.1 competition_level wiring); consumer pattern = useSettingsConfig
// .buyScoreWeightComponents (5th parallel loader added Day 91a.5 P2A).
// Pre-graduation 14 inline entries shipped via Days 55-58; the 7 is_available
// =true successor set: roi_pct + sales_velocity + profit_per_day +
// competition_level + ppu + roi_per_day + margin_pct (8 stub keys retired:
// bsr / returns_rate / buy_box_win_rate / demand_score / days_of_supply /
// review_score / price_trend pre-graduation + days_of_supply post). UI scope
// shift from 14 to 7 visible weights is intentional per R-Lock-7 substrate
// honesty (engine consumed only 7 wired keys both pre/post per Day 68 LEFT
// JOIN bswc gate; the 7 "ghost" keys were UI-visible but engine-ignored
// pre-graduation; post-graduation they're also UI-invisible).

export const CONSTRAINT_FIELDS: FilterDef[] = [
  { key: 'max_spend', label: 'Max Spend', unit: '$' },
  { key: 'max_units_per_sku', label: 'Max Units per SKU', unit: '' },
  { key: 'min_order_qty', label: 'Min Order Quantity', unit: '' },
  { key: 'my_products_only', label: 'My Products Only', unit: '', kind: 'boolean' },
];

/**
 * Day 98.1 §10 — the 9 advisory concentration guardrails (a CONSTRAINT_FIELDS
 * sibling, keyed by the approved GuardrailKey names). 7 are percentages,
 * max_dollar_single_sku is dollars, min_skus is a count. ADVISORY (highlight/
 * warn, never block); all default UNSET (opt-in per spec §10 "optional toggles"
 * — no invented starting %s). Evaluated client-side in evaluateGuardrails.
 */
export const GUARDRAIL_FIELDS: FilterDef[] = [
  { key: 'max_pct_single_sku', label: 'Max % Single SKU', unit: '%' },
  { key: 'max_dollar_single_sku', label: 'Max $ Single SKU', unit: '$' },
  { key: 'max_pct_brand', label: 'Max % Brand', unit: '%' },
  { key: 'max_pct_category', label: 'Max % Category', unit: '%' },
  { key: 'max_pct_marketplace', label: 'Max % Marketplace', unit: '%' },
  { key: 'max_pct_high_risk', label: 'Max % High Risk', unit: '%' },
  { key: 'max_pct_slow_movers', label: 'Max % Slow Movers', unit: '%' },
  { key: 'max_pct_new', label: 'Max % New Products', unit: '%' },
  { key: 'min_skus', label: 'Min # SKUs', unit: '' },
];

/**
 * Day 98.1 §11 — per-factor UI metadata for the Risk Factors sidebar section.
 * 3 live (data on disk); the 4 unavailable render disabled-with-hint (the Day-95
 * available:false + hint pattern). `threshold` names the numeric input a live
 * factor surfaces (low_demand is categorical → none).
 */
export interface RiskFactorDef {
  key: RiskFactorKey;
  label: string;
  available: boolean;
  hint?: string;
  threshold?: 'velocity_floor' | 'competition_ceiling';
}

export const RISK_FACTOR_FIELDS: RiskFactorDef[] = [
  { key: 'low_velocity', label: 'Low Velocity', available: true, threshold: 'velocity_floor' },
  {
    key: 'high_competition',
    label: 'High Competition',
    available: true,
    threshold: 'competition_ceiling',
  },
  { key: 'low_demand', label: 'Low Demand', available: true },
  {
    key: 'high_returns',
    label: 'High Returns',
    available: false,
    hint: 'Unavailable — no per-product returns-rate data yet.',
  },
  {
    key: 'amazon_as_seller',
    label: 'Amazon as Seller',
    available: false,
    hint: 'Unavailable — no products flagged Offered by Amazon yet.',
  },
  {
    key: 'stocking',
    label: 'Special Order',
    available: false,
    hint: 'Unavailable — stocking status not yet populated.',
  },
  {
    key: 'mfr_policy',
    label: 'Unknown Mfr Return Policy',
    available: false,
    hint: 'Unavailable — manufacturer return policy not yet recorded.',
  },
];

/**
 * Day 98.1 §11 — ratified default risk config: the 3 live factors ON, the 4
 * unavailable OFF, thresholds pre-filled (velocity_floor=30 ≈ 1 sale/day,
 * competition_ceiling=10 sellers; data-derived from prod). POBuilder resolves
 * prefs.risk ?? DEFAULT_RISK_CONFIG (Phase 4).
 */
export const DEFAULT_RISK_CONFIG: BuyingSessionRiskPref = {
  factors: {
    low_velocity: { enabled: true },
    high_competition: { enabled: true },
    low_demand: { enabled: true },
    high_returns: { enabled: false },
    amazon_as_seller: { enabled: false },
    stocking: { enabled: false },
    mfr_policy: { enabled: false },
  },
  thresholds: { velocity_floor: 30, competition_ceiling: 10 },
};

interface BuyingSessionPrefsShape {
  filters?: Array<{ key: string; default?: string | number | null }>;
  // Day 76 P5.1 — weights + constraints consumption per Phase 1E adjudication
  // (grep showed ZERO consumers of weights[].default; case (b) unfiled gap;
  // adding the loop so Phase 4 handleSaveDefaults UPSERT-by-key writes are
  // not decorative-only). NOTE: the proper consumer mechanism for weight
  // defaults at BuyingSession state init (read prefs.weights[].default →
  // seed local weights state) is a Day 77+ enhancement — Day 76 only writes
  // through Phase 4 + reads here for the InlineInput/drift dict, where weight
  // keys don't collide with filter/constraint keys (dormant-in-UI but
  // visible to future consumers).
  weights?: Array<{ key: string; default?: string | number | null }>;
  constraints?: Array<{ key: string; default?: string | number | boolean | null }>;
  guardrails?: Array<{ key: string; default?: string | number | boolean | null }>;
}

interface ParamResolveContext {
  distributorId?: string | null;
}

interface ParamResolverShape {
  resolveKey: (
    key: string,
    context?: ParamResolveContext
  ) => { param: ParamRow; level: string } | null;
}

/**
 * Compute the filter/constraint defaults map shown to the user in
 * InlineInput display + used by Day 73 drift detection. Resolution
 * order matches ParamsSidebar's prior in-line derivation
 * (ParamsSidebar.tsx:363-379, pre-Day-73): params hierarchy first,
 * then per-user buyingSessionPrefs.filters[].default override.
 *
 * Pure function — caller supplies the resolver + prefs; no hook
 * dependency. Day 73 P4.1 β-extraction per Phase 0 Decision 4.4.
 */
export function computeParamDefaults(
  resolver: ParamResolverShape,
  activeDistributorId: string | null | undefined,
  buyingSessionPrefs: BuyingSessionPrefsShape | null | undefined
): Record<string, string> {
  const defaults: Record<string, string> = {};
  const allFilterKeys = FILTER_FIELDS.map((f) => f.key);
  const allConstraintKeys = CONSTRAINT_FIELDS.map((c) => c.key);
  const allGuardrailKeys = GUARDRAIL_FIELDS.map((g) => g.key);
  for (const key of [...allFilterKeys, ...allConstraintKeys, ...allGuardrailKeys]) {
    const resolved = resolver.resolveKey(key, {
      distributorId: activeDistributorId ?? null,
    });
    const value = resolved?.param?.value;
    if (value != null) {
      defaults[key] = String(value);
    }
  }
  for (const f of buyingSessionPrefs?.filters ?? []) {
    if (f.default != null) defaults[f.key] = String(f.default);
  }
  // Day 76 P5.1 — weights + constraints default override (Phase 1E).
  for (const w of buyingSessionPrefs?.weights ?? []) {
    if (w.default != null) defaults[w.key] = String(w.default);
  }
  for (const c of buyingSessionPrefs?.constraints ?? []) {
    if (c.default != null) defaults[c.key] = String(c.default);
  }
  // Day 98.1 — guardrail defaults (same fold as constraints; not params-backed).
  for (const g of buyingSessionPrefs?.guardrails ?? []) {
    if (g.default != null) defaults[g.key] = String(g.default);
  }
  return defaults;
}

/**
 * Day 98.5 F5 (🔴 BACKLOG L15) — the single funnel every `buying_session_prefs`
 * writer routes through, so no writer can silently drop a top-level prefs key.
 * Shallow merge is correct because each top-level key is single-owner (filters/
 * weights ← Settings BSP savePrefs + handleSaveDefaults; constraints/guardrails ←
 * handleSaveDefaults; risk ← persistRisk; restock ← useRestockForecast): `patch`
 * overwrites only the key(s) its writer owns and the rest of `existing` survives
 * untouched — no deep merge needed. The `{filters:[],weights:[]}` fallback keeps
 * the filters+weights-required invariant when `existing` is null. Routing ALL
 * writers through this (not just the two former offenders) is the guard: a future
 * writer cannot reintroduce the drop (see #DBN-98b-F5 / Day 98b Tier-2).
 */
export function mergePrefs(
  existing: BuyingSessionPrefs | null | undefined,
  patch: Partial<BuyingSessionPrefs>
): BuyingSessionPrefs {
  return { ...(existing ?? { filters: [], weights: [] }), ...patch };
}

/**
 * UPSERT current session filter/weight/constraint values into the
 * BuyingSessionPrefs JSONB shape. Preserves visibility/order from
 * Settings → BSP; updates `default` field per OQ5 (a) lock 2026-05-17.
 * Day 76 P5.1 B.3.
 *
 * Per-class value semantics:
 *  - filters: numeric only; empty/non-numeric → null (preserve existing
 *    entry as-is, don't overwrite default with garbage)
 *  - weights: ENABLED-only per Sol lock 2026-05-17. Disabled weights pass
 *    through with no default change. Rationale: disabled weights aren't
 *    part of "my preferred setup"; save-defaults captures active config,
 *    not suppression set.
 *  - constraints: kind-aware. boolean kind (my_products_only) accepts
 *    'true'/'false' strings; numeric kinds parse via Number().
 *
 * UPSERT semantic: if no current value for a key with an existing pref
 * entry, the entry is preserved as-is (existing default field unchanged).
 * This matches "save current values as your defaults" — empty session
 * input doesn't blow away a prior saved default.
 */
export function buildSaveDefaultsPayload(
  bswcRows: BuyScoreWeightComponent[],
  existing: BuyingSessionPrefs | null | undefined,
  filterValues: Record<string, string | undefined>,
  weightValues: Record<string, { enabled: boolean; weight: string } | undefined>,
  constraintValues: Record<string, string | undefined>,
  // Day 98.1 — current Guardrails-section values; the existing 5-arg call site
  // (BuyingSession.tsx) defaults to {} until Phase 4 wires the section state.
  guardrailValues: Record<string, string | undefined> = {}
): BuyingSessionPrefs {
  const parseNumeric = (raw: string | undefined): number | null => {
    if (raw == null || raw === '') return null;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  };

  const parseFilterValue = (key: string): number | null => parseNumeric(filterValues[key]);

  const parseWeightValue = (key: string): number | null => {
    const entry = weightValues[key];
    if (!entry?.enabled) return null;
    return parseNumeric(entry.weight);
  };

  const parseConstraintValue = (key: string): string | number | boolean | null => {
    const raw = constraintValues[key];
    if (raw == null || raw === '') return null;
    const def = CONSTRAINT_FIELDS.find((c) => c.key === key);
    if (def?.kind === 'boolean') return raw === 'true';
    return parseNumeric(raw);
  };

  // Generic UPSERT-by-key. Existing entries first (preserves visibility/
  // order); new entries appended for source-order keys with non-null values.
  const upsertFilters = (): BuyingSessionFilterPref[] => {
    const seen = new Set<string>();
    const result: BuyingSessionFilterPref[] = [];
    for (const row of existing?.filters ?? []) {
      const value = parseFilterValue(row.key);
      result.push(value !== null ? { ...row, default: value } : row);
      seen.add(row.key);
    }
    FILTER_FIELDS.forEach((src) => {
      if (seen.has(src.key)) return;
      const value = parseFilterValue(src.key);
      if (value !== null) {
        result.push({ key: src.key, visible: true, order: result.length, default: value });
      }
    });
    return result;
  };

  const upsertWeights = (): BuyingSessionWeightPref[] => {
    const seen = new Set<string>();
    const result: BuyingSessionWeightPref[] = [];
    for (const row of existing?.weights ?? []) {
      const value = parseWeightValue(row.key);
      result.push(value !== null ? { ...row, default: value } : row);
      seen.add(row.key);
    }
    bswcRows
      .filter((r) => r.is_available)
      .forEach((src) => {
        if (seen.has(src.key)) return;
        const value = parseWeightValue(src.key);
        if (value !== null) {
          result.push({ key: src.key, visible: true, order: result.length, default: value });
        }
      });
    return result;
  };

  const upsertConstraints = (): BuyingSessionConstraintPref[] => {
    const seen = new Set<string>();
    const result: BuyingSessionConstraintPref[] = [];
    for (const row of existing?.constraints ?? []) {
      const value = parseConstraintValue(row.key);
      result.push(value !== null ? { ...row, default: value } : row);
      seen.add(row.key);
    }
    CONSTRAINT_FIELDS.forEach((src) => {
      if (seen.has(src.key)) return;
      const value = parseConstraintValue(src.key);
      if (value !== null) {
        result.push({ key: src.key, visible: true, order: result.length, default: value });
      }
    });
    return result;
  };

  // Day 98.1 — guardrails fold exactly like constraints (numeric only).
  const upsertGuardrails = (): BuyingSessionConstraintPref[] => {
    const seen = new Set<string>();
    const result: BuyingSessionConstraintPref[] = [];
    for (const row of existing?.guardrails ?? []) {
      const value = parseNumeric(guardrailValues[row.key]);
      result.push(value !== null ? { ...row, default: value } : row);
      seen.add(row.key);
    }
    GUARDRAIL_FIELDS.forEach((src) => {
      if (seen.has(src.key)) return;
      const value = parseNumeric(guardrailValues[src.key]);
      if (value !== null) {
        result.push({ key: src.key, visible: true, order: result.length, default: value });
      }
    });
    return result;
  };

  return {
    filters: upsertFilters(),
    weights: upsertWeights(),
    constraints: upsertConstraints(),
    guardrails: upsertGuardrails(),
    // Risk config passes through untouched — set by the Risk Factors UI directly
    // (structured, not a {key,default} array), like restock.
    risk: existing?.risk,
    // Restock state passes through untouched — different scope, owned by Restock.tsx
    restock: existing?.restock,
  };
}

/**
 * Capture the full params hierarchy state at a session-start moment for
 * the buying_sessions.param_snapshot audit trail. Used by
 * useBuyingSession.createSession + saveAsDraft per ADR Decision 27 two-
 * JSONB model + Day 54 follow-up closure (Sol OQ4 + option (a) re-capture
 * locks 2026-05-17). Re-captured at saveAsDraft because the draft IS the
 * persisted entity in the lifecycle — capture-at-draft reflects the
 * buyer's commit-intent moment, not stale exploration state.
 *
 * Shape (Sol Phase 5 adjudication 2026-05-17 — option (ii) full-state):
 * captures EVERY param at level='business' plus level='distributor'
 * overrides for the active distributor. No BUYING_KEYS allowlist —
 * chat-side's draft 31-key set was 24/31 fictional (filter/weight/
 * constraint keys are session-state-only, not params-backed). Full-state
 * capture eliminates allowlist maintenance burden + auto-includes new
 * params as they land + is forward-compat clean with Day 82 P6.1
 * provenance system.
 *
 * Resolution chain mirrors useParamStore.resolveKey (distributor →
 * business). Per Day 71 close-out, prod has 0 distributor-level
 * overrides today; future overrides land via Day 63a Formula Editor or
 * Day 82 P6.1 provenance writes. Snapshot rows sort by level then key
 * for diff-stability across sessions.
 */
interface ParamSnapshotEntry {
  key: string;
  value: number | string | null;
  level: string;
  source: string | null;
}

export interface ParamSnapshot {
  captured_at: string;
  distributor_id: string | null;
  params: ParamSnapshotEntry[];
}

export function buildParamSnapshot(
  allParams: ParamRow[],
  activeDistributorId: string | null
): ParamSnapshot {
  const relevant = allParams.filter((p) => {
    // Business-level defaults — always captured
    if (p.level === 'business' && p.entity_id === null) return true;
    // Distributor-level overrides — only for the active distributor
    if (
      p.level === 'distributor' &&
      activeDistributorId !== null &&
      p.entity_id === activeDistributorId
    ) {
      return true;
    }
    return false;
  });
  const params: ParamSnapshotEntry[] = relevant
    .map((p) => ({
      key: p.key,
      value: p.value,
      level: p.level,
      source: p.source ?? null,
    }))
    .sort((a, b) => a.level.localeCompare(b.level) || a.key.localeCompare(b.key));
  return {
    captured_at: new Date().toISOString(),
    distributor_id: activeDistributorId,
    params,
  };
}
